import { TrendingUp, Users, Award } from "lucide-react";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { IconBox } from "../ui/IconBox";
import BgImg from "../../assets/images/Pitch.png";

const Hero = () => {
  const GOOGLE_FORM_URL = "https://forms.gle/cze2oFduemkLYpbb9";

  const features = [
    {
      icon: TrendingUp,
      title: "Funding",
      description: "Connect with angels & VCs",
    },
    {
      icon: Users,
      title: "1:1 Meetings",
      description: "Private investor sessions",
    },
    {
      icon: Award,
      title: "Pitch Day",
      description: "Pitch on stage + feedback",
    },
  ];

  // Function to open Google Form in new tab
  const openGoogleForm = () => {
    window.open(GOOGLE_FORM_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="relative min-h-[90vh] flex items-center pt-20 pb-6 px-6 bg-primary text-textPrimary overflow-hidden scroll-mt-24 lg:mt-10">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src={BgImg} 
          alt="BeAshark Background" 
          className="absolute inset-0 w-full h-full object-cover" 
        />
        <div className="absolute inset-0 bg-black/40"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto py-20">
        {/* Hero content with event info on right */}
        <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-16 -mt-8">
          {/* Left side - Main content */}
          <div className="flex-1 max-w-2xl lg:max-w-3xl">
            {/* Mobile: Adjusted typography, Desktop: Same as before */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight space-y-2">
              <span className="block animate-fadeUp">Pitch Your Startup.</span>
              <span className="block animate-fadeUp delay-150">
                Meet the Investors.
              </span>
              <span className="block animate-fadeUp delay-300 text-bluePrimary">
                Get Funded.
              </span>
            </h1>

            <p className="text-lg md:text-xl text-textMuted mb-8 leading-relaxed">
              Join AQT Direct — a founder-first pitch event that connects
              ambitious startups with active investors, mentors and media
              exposure.
            </p>

            {/* NOMINATE NOW BUTTON - Mobile: full width, Desktop: auto width */}
            <div className="flex justify-start">
              <Button
                onClick={openGoogleForm}
                className="bg-gradient-to-r from-bluePrimary to-blueHover hover:from-blueHover hover:to-blue-800 
                          text-white px-8 md:px-10 py-4 rounded-xl shadow-lg hover:shadow-xl 
                          transition-all duration-300 transform hover:-translate-y-1 
                          text-base md:text-lg font-semibold w-full md:w-auto"
                size="lg"
              >
                Nominate Now
              </Button>
            </div>
          </div>

          {/* Right side - Event info - Mobile: below, Desktop: right side */}
          <div className="w-full lg:w-72 lg:ml-auto -mt-6 sm:mt-0">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4 md:p-6">
              {/* Date Section */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-bluePrimary/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 md:w-5 md:h-5 text-bluePrimary" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <div className="text-goldPrimary font-semibold text-xs md:text-sm uppercase tracking-wider truncate">Event Date</div>
                  <div className="text-white font-bold text-base md:text-lg truncate">31 January 2026</div>
                </div>
              </div>

              {/* Time Section - New Addition */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 md:w-5 md:h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <div className="text-green-400 font-semibold text-xs md:text-sm uppercase tracking-wider truncate">Time</div>
                  <div className="text-white font-bold text-base md:text-lg">6:00 PM - 9:00 PM</div>
                </div>
              </div>

              {/* Venue Section */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-goldPrimary/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 md:w-5 md:h-5 text-goldPrimary" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <div className="text-bluePrimary font-semibold text-xs md:text-sm uppercase tracking-wider truncate">Venue</div>
                  <div className="text-white font-bold text-base md:text-lg">
                    Holiday Inn,<br />Gurugram
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FEATURE CARDS - Mobile: 1 column, Desktop: 3 columns */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl">
          {features.map((feature, index) => (
            <Card
              key={index}
              className="p-4 md:p-6 rounded-xl md:rounded-2xl bg-tertiary border border-borderLight shadow-card 
                       hover:shadow-xl transition-all hover:bg-secondary/60 group"
              hover
            >
              <div className="flex items-start gap-3">
                <IconBox
                  icon={feature.icon}
                  size="sm"
                  className="mb-0 rounded-lg md:rounded-xl bg-blueTint text-bluePrimary 
                           group-hover:text-white transition-colors flex-shrink-0"
                />
                <div className="min-w-0">
                  <h3 className="text-base md:text-lg font-bold text-black mb-1 
                               group-hover:text-white transition-colors truncate">
                    {feature.title}
                  </h3>
                  <p className="text-black text-xs md:text-sm
                              group-hover:text-white transition-colors truncate">
                    {feature.description}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;